import React from 'react';
import styled from 'styled-components';

import useTranslation from '@src/hooks/useTranslation';
import useCurrentCourse from '@src/hooks/useCurrentCourse';
import { isPublished, isDiscontinued, formatCourseTimeMinutes } from '@src/utils/course';
import {
	COURSE_UNIT_TYPE_SECTION,
	COURSE_UNIT_TYPE_HOMEWORK,
	COURSE_UNIT_TYPE_QUIZ,
	COURSE_UNIT_TYPE_SURVEY,
} from '@src/const/course';

import Header from '@src/components/common/header';
import Collapse from './collapse';

import { BlockWrapper } from '../styled';

const ChaptersWrapper = styled.div`
	border-top: 1px solid ${props => `${props.theme.colors.primary}`};
	border-bottom: 1px solid ${props => `${props.theme.colors.primary}`};
	& > div:last-child > div {
		border-bottom: none;
	}
`;

const CourseOutlineWrapper = styled(BlockWrapper)`
	.course-time__bold {
		font-weight: 500;
		margin: 0px 4px;
		font-size: 20px;
		color: ${props => props.theme.colors.primaryDark};
	}
`;

const CourseTimeContainer = styled.div`
	display: flex;
	padding-bottom: 16px;
	@media
	${props => props.theme.device.smTablet},
	${props => props.theme.device.mobile}
	{
		flex-direction: column;
		padding-left: 20px;
	}

	@media ${props => props.theme.device.mobile} {
		padding: 0 0px 16px 8px;
	}
`;

const CourseTimeNote = styled.div`
	color: #676767;
	font-size: 14px;
	margin-top: 7px;
	font-weight: 300;
`;

const CourseTime = styled.div`
	letter-spacing: 1px;
	color: #333333;
	white-space: nowrap;
	margin-right: 8px;
`;

const CourseOutline = (): JSX.Element => {
	const { t } = useTranslation();
	const { currentCourse } = useCurrentCourse();
	const { chapters, estimated_length, video_duration_seconds, status, duration_memo } = currentCourse;

	const courseTime = (isPublished(status) || isDiscontinued(status)) ?
		formatCourseTimeMinutes(video_duration_seconds) :
		estimated_length;
	const chapterWithPreviableVideo = chapters.map(
		chapter => !!chapter.units.find(unit => (unit.type === COURSE_UNIT_TYPE_SECTION) && unit.video_link?.link),
	);
	const firstIndex = chapterWithPreviableVideo.findIndex(item => item);
	const defaultExpandIndex = (firstIndex < 0) ? 0 : firstIndex;

	return (
		<CourseOutlineWrapper id="course_trailer">
			<Header text={t('course:information:course_trailer')} />
			<CourseTimeContainer>
				<CourseTime
					key={new Date().getTime()}
					dangerouslySetInnerHTML={{
						__html: t('course:information:course_time_estimate', { minutes: courseTime }),
					}}
				/>
				<CourseTimeNote>
					{duration_memo}
				</CourseTimeNote>
			</CourseTimeContainer>
			<ChaptersWrapper>
				{chapters.map(({ id, name, units }, chapterIndex) => {
					let sectionIndex = 0;
					let homeworkIndex = 0;
					let quizIndex = 0;
					let unitIndex = 0;
					let surveyIndex = 0;
					const chapter = {
						name,
						chapterIndex: chapterIndex + 1,
						units: units.map(unit => {
							if (unit.type === COURSE_UNIT_TYPE_SECTION) {
								sectionIndex += 1;
								unitIndex = sectionIndex;
							}
							if (unit.type === COURSE_UNIT_TYPE_HOMEWORK) {
								homeworkIndex += 1;
								unitIndex = homeworkIndex;
							}
							if (unit.type === COURSE_UNIT_TYPE_QUIZ) {
								quizIndex += 1;
								unitIndex = quizIndex;
							}
							if (unit.type === COURSE_UNIT_TYPE_SURVEY) {
								surveyIndex += 1;
								unitIndex = surveyIndex;
							}

							return ({
								index: unitIndex,
								videoLink: unit.using_embedded_video ? unit.embedded_video_url : unit.video_link?.link,
								name: unit.name,
								previewable: unit.previewable,
								type: unit.type,
							});
						}),
					};

					return (
						<Collapse
							key={id}
							chapter={chapter}
							defaultExpand={defaultExpandIndex === chapterIndex}
						/>
					);
				})}
			</ChaptersWrapper>
		</CourseOutlineWrapper>
	);
};

export default CourseOutline;
